// Inbound shop-line SMS alerts. The browser already hears `threads` where
// source is skyway-phone. phoneSmsInbound stamps lastMessageDirection
// "inbound" and a new lastMessageId. Outbound sends stamp "outbound".
// Delivery status callbacks update the message doc only, so a thread
// listener never sees them as a new text.

import { isSkywayPhoneThread, messageTime, threadTitle } from './phone';

export function phoneThreadHref(threadId) {
  const id = String(threadId || '').trim();
  if (!id) return '/phone';
  return `/phone/${encodeURIComponent(id)}`;
}

export function isInboundPhoneThread(thread) {
  if (!isSkywayPhoneThread(thread)) return false;
  return String(thread.lastMessageDirection || '').trim().toLowerCase() === 'inbound';
}

// Identity of the inbound text currently at the top of the thread.
// Empty when the latest activity is outbound or not a real message.
export function inboundFingerprint(thread) {
  if (!isInboundPhoneThread(thread)) return '';
  const id = String(thread.lastMessageId || '').trim();
  const at = messageTime(thread.lastMessageDate)?.getTime() || 0;
  const body = String(thread.lastMessageBody || '');
  if (id) return `${thread.id}|${id}`;
  if (!at && !body) return '';
  return `${thread.id}|${at}|${body}`;
}

export function unreadPhoneCount(threads) {
  return (threads || []).filter(thread => isSkywayPhoneThread(thread) && thread.hasUnread === true).length;
}

export function indexInboundFingerprints(threads) {
  const next = new Map();
  (threads || []).forEach(thread => {
    if (!thread || !thread.id || !isSkywayPhoneThread(thread)) return;
    next.set(thread.id, inboundFingerprint(thread));
  });
  return next;
}

// `previous` is the fingerprint map from the last snapshot. A thread is a
// new inbound only when its fingerprint is non-empty and different.
// The first snapshot passes an empty map and the caller drops the result
// so texts already waiting do not alert on login.
export function inboundArrivals(previous, threads) {
  const prior = previous instanceof Map ? previous : new Map();
  const next = indexInboundFingerprints(threads);
  const arrivals = [];
  next.forEach((fp, id) => {
    if (!fp || prior.get(id) === fp) return;
    const thread = (threads || []).find(row => row && row.id === id);
    if (thread) arrivals.push(thread);
  });
  arrivals.sort((a, b) => {
    const tb = messageTime(b && b.lastMessageDate)?.getTime() || 0;
    const ta = messageTime(a && a.lastMessageDate)?.getTime() || 0;
    return tb - ta;
  });
  return { next, arrivals };
}

export function inboundAlertCopy(thread) {
  const who = threadTitle(thread);
  const preview = String(thread?.lastMessageBody || '').replace(/\s+/g, ' ').trim();
  return {
    threadId: thread.id,
    href: phoneThreadHref(thread.id),
    title: `New text from ${who}`,
    body: preview || 'Open Phone to read it',
  };
}

export function toastFromArrivals(arrivals) {
  const list = arrivals || [];
  if (!list.length) return null;
  const copy = inboundAlertCopy(list[0]);
  if (list.length === 1) return copy;
  const who = copy.title.replace(/^New text from /, '');
  return {
    ...copy,
    title: `${list.length} new texts`,
    body: `${who}: ${copy.body}`,
  };
}

// Skip the alert when this tab is already showing that thread.
// A background tab still alerts so the OS notification can bring it forward.
export function shouldSurfaceInboundAlert(threadId, view = {}) {
  const path = String(view.pathname || '').replace(/\/+$/, '');
  const expected = phoneThreadHref(threadId);
  const onThread = path === expected;
  if (!onThread) return true;
  return view.visibility === 'hidden';
}

export function readPhoneNotificationPermission(NotificationAPI) {
  const API = NotificationAPI !== undefined
    ? NotificationAPI
    : (typeof window !== 'undefined' ? window.Notification : undefined);
  if (!API || (typeof API !== 'function' && typeof API !== 'object')) return 'unsupported';
  const value = API.permission;
  if (value === 'granted' || value === 'denied' || value === 'default') return value;
  if (typeof API !== 'function') return 'unsupported';
  return 'default';
}

export async function requestPhoneNotificationPermission(NotificationAPI) {
  const API = NotificationAPI !== undefined
    ? NotificationAPI
    : (typeof window !== 'undefined' ? window.Notification : undefined);
  const current = readPhoneNotificationPermission(API);
  if (current !== 'default') return current;
  if (typeof API.requestPermission !== 'function') return current;
  try {
    const result = await API.requestPermission();
    if (result === 'granted' || result === 'denied' || result === 'default') return result;
  } catch (err) {
    return current;
  }
  return readPhoneNotificationPermission(API);
}

export function showInboundBrowserNotification(alert, {
  NotificationAPI,
  onClick,
  focus,
} = {}) {
  if (!alert || !alert.threadId) return null;
  const API = NotificationAPI !== undefined
    ? NotificationAPI
    : (typeof window !== 'undefined' ? window.Notification : undefined);
  if (readPhoneNotificationPermission(API) !== 'granted') return null;
  let note;
  try {
    note = new API(alert.title, {
      body: alert.body,
      tag: `skyway-phone-${alert.threadId}`,
    });
  } catch (err) {
    return null;
  }
  note.onclick = () => {
    try {
      if (typeof focus === 'function') focus();
      else if (typeof window !== 'undefined' && typeof window.focus === 'function') window.focus();
    } catch (err) {
      // The tab can still navigate if the browser refuses focus.
    }
    try { note.close(); } catch (err) { /* already closed */ }
    if (onClick) onClick(alert);
  };
  return note;
}

export function focusPhoneThread(threadId, history, focusWindow) {
  const href = phoneThreadHref(threadId);
  const path = history && history.location ? history.location.pathname : '';
  if (history && typeof history.push === 'function' && path !== href) history.push(href);
  const focus = focusWindow || (
    typeof window !== 'undefined' && typeof window.focus === 'function'
      ? () => window.focus()
      : null
  );
  try { if (focus) focus(); } catch (err) { /* focus is best-effort */ }
  return href;
}
